import {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box } from '@mui/material';

import { ButtonIcon } from 'components/general';
import { ResponsivePanPinchZoom } from 'components/responsivePanPinchZoom';
import { useCreateStore } from 'hooks/createStore';

import {
  BUTTON_STYLE,
  containerSx,
  GALLERY_IMAGE_ZOOM_FACTOR,
  getScaleControllersSx,
  transformOptions,
} from './styles';
import { FloatingCreateIssueButton } from './FloatingCreateIssueButton';
import { useShowCreateIssueButton } from './hooks/useShowCreateIssueButton';
import {
  ImageDisplayProps,
  ScaleControllersProps,
  ScaleState,
} from './types';

const DefaultImageDisplayOverlay = (): null => null;

export function ImageDisplay({
  source,
  alt,
  ImageDisplayOverlay = DefaultImageDisplayOverlay,
  imageRotation,
}: ImageDisplayProps): ReactElement | null {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const {
    show: showCreateIssueButton,
    availableSites,
    availableLevelsPerSite,
    initialValues,
    loadingLevels,
  } = useShowCreateIssueButton();
  
  const zoomControlsRef = useRef({
    zoomIn: () => {},
    zoomOut: () => {},
  });

  const scaleStore = useCreateStore<ScaleState>({
    scale: 1,
    maxScale: 1,
    minScale: 1,
  });

  const onTransformed = useCallback(
    (ref, { scale }: { scale: number }): void => {
      scaleStore.set({
        scale,
        minScale: ref.instance.props.minScale || 1,
        maxScale: ref.instance.props.maxScale || 1,
      });
      zoomControlsRef.current = {
        zoomIn: ref.zoomIn,
        zoomOut: ref.zoomOut,
      };
    },
    [scaleStore]
  );

  const imageRef = useRef(null);

  if (!source) return null;

  return (
    <Box
      sx={containerSx}
      ref={(el: HTMLDivElement | null) => setContainer(el)}
    >
      <ResponsivePanPinchZoom
        onTransformed={onTransformed}
        transformOptions={transformOptions}
        imageRef={imageRef}
        imageSrc={source}
        imageLoading={false}
        imageError={undefined}
        container={container}
        minimumScaleFactor={0}
        imageRotation={imageRotation}
        zoomFactor={GALLERY_IMAGE_ZOOM_FACTOR}
      >
        <ImageDisplayOverlay />
      </ResponsivePanPinchZoom>
      <FloatingCreateIssueButton
        availableSites={availableSites}
        availableLevelsPerSite={availableLevelsPerSite}
        initialValues={initialValues}
        loadingLevels={loadingLevels}
      />
      <ScaleControllers
        scaleStore={scaleStore}
        zoomRef={zoomControlsRef}
        offsetForCreateButton={showCreateIssueButton}
      />
    </Box>
  );
}

function ScaleControllers({
  zoomRef,
  scaleStore,
  offsetForCreateButton,
}: ScaleControllersProps): ReactElement {
  const [scale, setScale] = useState(scaleStore.get().scale);
  const [minScaleState, setMinScaleState] = useState(
    scaleStore.get().minScale
  );
  const [maxScaleState, setMaxScaleState] = useState(
    scaleStore.get().maxScale
  );

  useEffect(() => {
    scaleStore.subscribe(() => {
      setScale(scaleStore.get().scale);
      setMinScaleState(scaleStore.get().minScale);
      setMaxScaleState(scaleStore.get().maxScale);
    });
  }, [scaleStore]);

  return (
    <Box sx={getScaleControllersSx(offsetForCreateButton)}>
      <ButtonIcon
        style={BUTTON_STYLE}
        disabled={scale >= maxScaleState}
        onClick={() => zoomRef.current.zoomIn()}
      >
        <AddIcon />
      </ButtonIcon>
      <ButtonIcon
        style={BUTTON_STYLE}
        disabled={scale <= minScaleState}
        onClick={() => zoomRef.current.zoomOut()}
      >
        <RemoveIcon />
      </ButtonIcon>
    </Box>
  );
}
